import {
  WSPanel,
  WSText,
  WSButtons,
  WSButton,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import {
  BulkStatus,
  IBulkCollaboratorBatch,
  IBulkCollaboratorItem,
  IBulkCollaboratorItemCreate
} from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { PreventLeave } from "../../components/PreventLeave/PreventLeave";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useBulkUploadCollaboratorsImporter } from "../../Invoices/screens/bulkUploadCollaborators/bulkUploadCollaboratorsImporter";
import { useUpdateActivity } from "../../query/activity";
import { useUserId } from "../../query/hooks/helpers";
import { useUpdateBulkCollaboratorBatch } from "../../query/payments/mutations";
import {
  useBulkCollaboratorBatch,
  useBulkCollaboratorBatchItems
} from "../../query/payments/queries";
import { useActivities } from "../../query/users/queries";
import { get1099SetupProgress } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { paymentsService } from "../../services/payments";
import { bulkActionFactory } from "../../utils/bulkActionFactory";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import {
  csvCollaboratorsToBulkBatchCreateItems,
  IFailedBulkCollaboratorItemCreate,
  UploadCollaboratorWithFormW9
} from "./NEC1099FilingUploadCollaborators";
import { ImportSummary } from "./NEC1099FilingUploadOffPlatformPayments";

import styles from "./styles.module.scss";

export const NEC1099FilingUploadFailedCollaborators: React.FC<SetupFlowRouteComponentProps> = ({
  onNext,
  onBack,
  location
}) => {
  const match = useRouteMatch<{ batchId: string }>();
  const history = useHistory<{
    csvCollaborators?: UploadCollaboratorWithFormW9[];
    selectedCollaboratorGroupId: string;
    selectedCollaboratorGroupName: string;
    bulkCollaboratorBatchId: string;
    fileName?: string;
    flatFileBatchId?: string;
    failedCreateBatchItems?: IFailedBulkCollaboratorItemCreate[];
  }>();

  const {
    selectedCollaboratorGroupId,
    selectedCollaboratorGroupName,
    fileName: uploadFileName,
    flatFileBatchId
  } = history.location.state;
  const bulkCollaboratorBatchId = match.params.batchId;
  const userId = useUserId();
  const { openSnackbar } = useWSSnackbar();

  const [areItemsAddedToBatch, setAreItemsAddedToBatch] = useState(false);
  const [isUploadingBatchItems, setIsUploadingBatchItems] = useState(false);
  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const [isBatchUploadStarted, setIsBatchUploadStarted] = useState(false);
  const [isBatchFetchingFailed, setIsBatchFetchingFailed] = useState(false);
  const [csvCollaborators, setCsvCollaborators] = useState<
    UploadCollaboratorWithFormW9[]
  >([]);
  const [successCreateBatchItems, setSuccessCreateBatchItems] = useState<
    IBulkCollaboratorItem[]
  >([]);
  const [failedCreateBatchItems, setFailedCreateBatchItems] = useState<
    IFailedBulkCollaboratorItemCreate[]
  >([]);

  const bulkUploadCollaboratorsImporter = useBulkUploadCollaboratorsImporter(
    {
      label: selectedCollaboratorGroupName,
      value: selectedCollaboratorGroupId
    },
    {
      onClose: () => {
        setIsFlatfileLoading(false);
      },
      withFormW9: true
    }
  );

  const bulkCollaboratorBatchQuery = useBulkCollaboratorBatch(
    bulkCollaboratorBatchId,
    {
      enabled: isBatchUploadStarted && !isBatchFetchingFailed,
      refetchInterval: 3000
    }
  );

  const bulkCollaboratorBatchItemsQuery = useBulkCollaboratorBatchItems(
    bulkCollaboratorBatchId
  );
  const qActivity = useActivities(userId);

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updateBulkCollaboratorBatch,
    updateBulkCollaboratorBatchMeta
  ] = useUpdateBulkCollaboratorBatch();

  useEffect(() => {
    if (isUploadingBatchItems === false && failedCreateBatchItems.length > 0) {
      console.log("failedCreateBatchItems", failedCreateBatchItems);

      history.replace({
        pathname: "/member/1099-filing/set-up/upload-collaborators",
        state: {
          csvCollaborators,
          selectedCollaboratorGroupId,
          selectedCollaboratorGroupName,
          bulkCollaboratorBatchId
        }
      });
    }
  }, [isUploadingBatchItems, failedCreateBatchItems]);

  useEffect(() => {
    // stop polling if get batch fails
    if (bulkCollaboratorBatchQuery.isError) {
      setIsBatchFetchingFailed(true);
    }
    if (
      bulkCollaboratorBatchQuery.data &&
      bulkCollaboratorBatchQuery.data?.status === BulkStatus.Complete
    ) {
      setIsBatchUploadStarted(false);
    }
  }, [bulkCollaboratorBatchQuery.data?.status]);

  const bulkCreateBatchItemCollaborator = bulkActionFactory({
    onStart: () => {
      setIsUploadingBatchItems(true);
    },
    action: async (data: IBulkCollaboratorItemCreate) => {
      try {
        const response = await paymentsService.bulkCollaborator.batchItem.create(
          bulkCollaboratorBatchId,
          data
        );
        setSuccessCreateBatchItems(successCreateBatchItems => [
          ...successCreateBatchItems,
          {
            ...response
          }
        ]);
        return response;
      } catch (error) {
        setFailedCreateBatchItems(failedCreateBatchItems => [
          ...failedCreateBatchItems,
          {
            ...data,
            error:
              (error as AxiosError)?.response?.data.error ||
              "Internal server error"
          }
        ]);
      }
    },
    onFinish: () => {
      setAreItemsAddedToBatch(true);
      setIsUploadingBatchItems(false);
    }
  });

  const importData = async () => {
    setIsFlatfileLoading(true);
    const results = await bulkUploadCollaboratorsImporter();
    const _csvCollaborators = results.validData as Array<
      UploadCollaboratorWithFormW9
    >;
    setCsvCollaborators(_csvCollaborators);
    if (_csvCollaborators.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message: "Sorry, Looks like the uploaded spreadsheet is empty!"
      });
      return;
    }
    return _csvCollaborators;
  };

  // import corrected rows only
  const onImportPayments = async () => {
    const csvCollaborators = await importData();
    const batchItemsRequests = csvCollaboratorsToBulkBatchCreateItems(
      csvCollaborators || [],
      selectedCollaboratorGroupId
    );
    await bulkCreateBatchItemCollaborator.run(batchItemsRequests);
  };

  const onConfirmImport = async () => {
    await updateBulkCollaboratorBatch(
      {
        id: bulkCollaboratorBatchId,
        data: {
          status: BulkStatus.Pending
        }
      },
      {
        onSuccess: () => {
          // BE will start importing collaborators once we patch
          // BulkPayableBatch to Pending status
          setIsBatchUploadStarted(true);
        }
      }
    );
  };

  const onContinue = async () => {
    const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
    await updateActivity(
      {
        flows: {
          nec1099Setup: {
            ...nec1099SetupProgress,
            currentStep: 3
          }
        }
      },
      {
        onSuccess: () => {
          onNext();
        }
      }
    );
  };

  const bulkCollaboratorBatch = bulkCollaboratorBatchQuery.data as IBulkCollaboratorBatch;

  return (
    <NEC1099FilingContainer title="Import Collaborators">
      <PreventLeave
        isEnabled={
          successCreateBatchItems.length > 0 ||
          failedCreateBatchItems.length > 0
        }
        promptMessage="Your collaborators have already been uploaded and are getting processed to be imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
      />
      <WSQueries queries={{ bulkCollaboratorBatchItemsQuery }}>
        {({
          bulkCollaboratorBatchItemsQuery: {
            data: { data: bulkCollaboratorBatchItems }
          }
        }) => {
          return (
            <WSPanel>
              <WSText.Heading3 mb="M">
                Upload the corrected spreadsheet
              </WSText.Heading3>
              <WSText.Paragraph color="grey600" mb="M">
                Correct any wrong or missing information in the spreadsheet,
                save, then click the "Import collaborators" button to try again.
                Note:{" "}
                {bulkCollaboratorBatch?.statistics?.itemsProcessed
                  ? bulkCollaboratorBatch?.statistics?.itemsProcessed
                  : "some"}{" "}
                rows that were successfully imported will be preserved during
                this process.
              </WSText.Paragraph>

              <ImportSummary
                summary={bulkCollaboratorBatch}
                isUploadStarted={isBatchUploadStarted}
                totalFailedBatchItems={failedCreateBatchItems.length}
                totalImportedRows={
                  bulkCollaboratorBatchItems.length + csvCollaborators.length
                }
              />

              <WSButtons forceFullWidth>
                {isBatchUploadStarted ||
                [BulkStatus.Complete, BulkStatus.Failed].includes(
                  bulkCollaboratorBatch?.status
                ) ? (
                  <WSButton.Primary
                    mt="L"
                    className={styles.actionBtn}
                    fullWidth
                    name="continueToCalculationSettings"
                    onClick={onContinue}
                    loading={updateActivityMeta.isLoading}
                    disabled={isBatchUploadStarted}
                  >
                    Continue to off-platform payments
                  </WSButton.Primary>
                ) : areItemsAddedToBatch ? (
                  <WSButton.Primary
                    mt="L"
                    className={styles.actionBtn}
                    fullWidth
                    name="bulkUploadCollaborators"
                    onClick={onConfirmImport}
                    loading={
                      isBatchUploadStarted ||
                      updateBulkCollaboratorBatchMeta.isLoading
                    }
                  >
                    Upload
                  </WSButton.Primary>
                ) : (
                  <WSButton
                    icon="upload"
                    onClick={onImportPayments}
                    loading={
                      isFlatfileLoading ||
                      updateBulkCollaboratorBatchMeta.isLoading
                    }
                  >
                    Import collaborators
                  </WSButton>
                )}
                <WSButton.Link onClick={onNext}>Skip for now</WSButton.Link>
              </WSButtons>
            </WSPanel>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
